import {useEffect, useLayoutEffect} from 'react';
import { observer } from 'mobx-react';

import ClickableLink from "../../../utilities/ClickableLink";
import { initFacebookSdk } from "../../../../utils/FacebookUtils";
import {ErrorBoundary} from "../../../utilities/ErrorBoundary";
import {isJson} from "../../../admin/spinup/utilities";
import {IBlockComponentProps} from "../_Block";

const FacebookContent = (props: FacebookContentProps) => {
    const { linkType } = props.blockObj;
    const facebookUrl = props.url;

    const params: {
        href: string,
        width: null | number,
        height: null | number,
        tabs: null | boolean,
        small_header: null | boolean,
        hide_cover: null | boolean,
        show_facepile: null | boolean,
        show_text: null | boolean,
        allowfullscreen: null | boolean,
        autoplay: null | boolean,
        show_captions: null | boolean,
        adapt_container_width: boolean,
        show_posts: null | boolean,
    } = {
        href: facebookUrl,
        width: null,
        height: null,
        tabs: null,
        small_header: null,
        hide_cover: null,
        show_facepile: null,
        show_text: null,
        allowfullscreen: null,
        autoplay: null,
        show_captions: null,
        adapt_container_width: false,
        show_posts: null,
    };

    let blockText = '';

    switch (linkType) {
        // the types that PHP returns are page, post, video, and error, so we need to handle all of them.
        case 'page':
            blockText = 'Go to the Facebook Page';
            params.width = props.dimensions.width > 500 ? 500 : props.dimensions.width;
            params.height = props.dimensions.height;
            params.small_header = true;
            params.hide_cover = true;
            params.show_facepile = true;
            params.show_posts = true;
            break;

        case 'post':
            blockText = 'View this Post on Facebook';
            params.show_text=true;
            break;

        case 'video':
            blockText = 'View this Video on Facebook';
            params.allowfullscreen = true;
            params.autoplay = false;
            params.show_captions = true;
            params.show_text = true;
            break;

        case 'error':
        default:
            // if an error, it's coming from PHP so check the regexs in PHP on how we identify linkType.
            blockText = 'There is an error displaying this page. Click to view.'
            break;
    }

    return <div className={"fb-"+linkType}
                data-href={params.href}
                data-adapt-container-width={params.adapt_container_width}
                data-width={params.width}
                data-height={params.height}
                data-small-header={params.small_header}
                data-hide-cover={params.hide_cover}
                data-show-facepile={params.show_facepile}
                data-allow-full-screen={params.allowfullscreen}
                data-autoplay={params.autoplay}
                data-show-captions={params.show_captions}
                data-show-text={params.show_text}
                data-show-posts={params.show_posts}
    >
        <blockquote cite={facebookUrl} className="fb-xfbml-parse-ignore">
            <ClickableLink href={facebookUrl}>{blockText}</ClickableLink>
        </blockquote>
    </div>
};

const FacebookBlock = observer((props: FacebookBlockProps) => {
    const { blockObj, blockRef, htmlId } = props;
    // in PHP the json_data we get back is a string... but it sends jsonObj, so set that if it exists.
    let json_data = props.blockObj.blockModel.jsonObj ? props.blockObj.blockModel.jsonObj : props.blockObj.blockModel.json_data
    if (typeof json_data === 'string' && isJson(json_data)) json_data = JSON.parse(json_data)
    const facebookUrl = json_data?.block?.settings?.url;
    const block = blockRef.current;
    // block is not null if it is a part of the grid i.e. not in the preview/add/edit modal
    const dimensions = {
        height: (props.sizeY * 145) - 20,
        width: (props.sizeX * 290) - 20,
    }

    useLayoutEffect(() => {
        if (window.FB?.XFBML) {
            if (block !== null) {
                window.FB.XFBML.parse(block);
            } else {
                const sub = document.getElementById(htmlId);
                window.FB.XFBML.parse(sub);
            }
        }
    }, [facebookUrl, props.sizeX, props.sizeY]);

    useEffect(() => {
        const disposer = initFacebookSdk();
        return () => {
            // disposer can be undefined
            if (typeof disposer === "function") {
                disposer();
            }
        };
    }, []);

    return <ErrorBoundary>
        <div className="sb-facebook-embed-plugin">
            <FacebookContent blockObj={blockObj} dimensions={dimensions} url={facebookUrl}/>
        </div>
    </ErrorBoundary>
});

interface FacebookContentProps {
    blockObj: {
        linkType: string,
    },
    dimensions: {
        width: number,
        height: number,
    },
    url: string,
}

interface FacebookBlockProps extends IBlockComponentProps {
    blockObj: IFacebookBlockObj,
}

export default FacebookBlock;
